import { useRef, type VideoHTMLAttributes } from 'react';
import { useWholeFile } from '@/lib/wholeFile';

/**
 * A <video> that downloads its whole file into memory and then plays from
 * there, so a poor connection does not keep stopping playback (see useWholeFile).
 */
export function BufferedVideo({
  src,
  cacheKey,
  elRef,
  ...rest
}: Omit<VideoHTMLAttributes<HTMLVideoElement>, 'src' | 'preload'> & {
  src: string | undefined;
  /** Names the file when its URL changes between loads (signed URLs). */
  cacheKey?: string;
  elRef?: (el: HTMLVideoElement | null) => void;
}) {
  const ref = useRef<HTMLVideoElement | null>(null);
  const whole = useWholeFile(ref, src, cacheKey ?? src);
  return (
    <video
      {...rest}
      ref={(el) => {
        ref.current = el;
        elRef?.(el);
      }}
      src={whole.src}
      // While the whole file downloads, the element does not fetch it a second time unless played.
      preload={whole.progress !== null ? 'metadata' : 'auto'}
    />
  );
}
